/**
 * 정산/구독 날짜 입력 검증 (2026-09-02 인시던트 대응).
 *
 * ## 왜 프론트에도 두는가
 *
 * 관리자가 구독 추가 중 **첫 유료일의 연도를 잘못 입력**했더니, 서버가 그대로 저장했고
 * `channel_subscription.billing_start_at`(MySQL DATETIME, 범위 1000~9999)에
 * `0000-00-00 00:00:00` 이 들어갔다. 그 뒤로 그 행을 읽는 **모든 화면이 500**(`Zero date value
 * prohibited`)이 나서 전체 고객·고객사 상세가 통째로 열리지 않았다.
 *
 * 서버에도 검증을 넣었다(`SubscriptionDateValidator` → 400 + 사유). 그래도 여기서 한 번 더 막는 이유:
 *  - `<input type="date">` 는 **연도 1자리도 통과시킨다**(`0025-08-01`). 사람은 자기가 무엇을 잘못
 *    입력했는지 모른 채 저장 버튼을 누른다.
 *  - 왕복 없이 그 자리에서 알려주는 편이 낫다. 서버 400 은 최종 방어선이고, 이건 **오타 즉시 안내**다.
 *
 * `min`/`max` 속성만으로는 부족하다 — 브라우저가 표시로는 막아도 값 자체는 들어간다.
 */

/** 날짜 입력 `min`/`max` 에 그대로 넣는 값. 서버 `SubscriptionDateValidator` 와 같은 범위여야 한다. */
export const MIN_BILLING_DATE = '2000-01-01';
export const MAX_BILLING_DATE = '2100-12-31';

const MIN_YEAR = 2000;
const MAX_YEAR = 2100;

/**
 * 첫 유료일(YYYY-MM-DD) 검증. 비어 있으면 통과한다 — **의도적으로 비울 수 있는 칸**이고,
 * 비우면 서버가 채널 '과금 기준일'로 폴백한다.
 *
 * @throws {Error} 형식이나 연도가 올바르지 않을 때 (호출부의 `run()` 이 화면에 문구를 띄운다)
 */
export const assertBillingStartAt = (value) => {
  const raw = (value ?? '').trim();
  if (!raw) return;

  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!m) throw new Error(`첫 유료일 '${raw}' 형식이 올바르지 않습니다. YYYY-MM-DD 로 입력해 주세요.`);

  const year = Number(m[1]);
  if (year < MIN_YEAR || year > MAX_YEAR) {
    throw new Error(
      `첫 유료일의 연도(${m[1]})가 올바르지 않습니다. ${MIN_YEAR}~${MAX_YEAR} 사이로 입력해 주세요.`
      + ` 연도 네 자리를 다시 확인해 주세요. (입력값 '${raw}')`
    );
  }
  // 존재하지 않는 날짜(2026-02-31 등)도 거른다 — Date 가 다음 달로 넘겨 버리므로 되돌려 비교한다.
  const d = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(d.getTime()) || d.getMonth() + 1 !== Number(m[2]) || d.getDate() !== Number(m[3])) {
    throw new Error(`첫 유료일 '${raw}' 은 없는 날짜입니다.`);
  }
};

/**
 * 적용 시작월/종료월(YYYY-MM) 검증.
 *
 * ⚠️ 이 칸들은 `varchar(7)` 이고 활성 여부를 **문자열 비교**로 판정한다
 * (`effective_from <= '2026-08'`). `2026-8` 처럼 자리수가 다르면 오류 없이
 * **그 구독이 조용히 정산에서 빠진다** — 0000 날짜보다 발견이 더 어렵다.
 *
 * @param {boolean} required 비어 있으면 거절할지(적용 시작월은 필수, 종료월은 선택)
 */
export const assertBillingMonth = (value, label, required = true) => {
  const raw = (value ?? '').trim();
  if (!raw) {
    if (required) throw new Error(`${label}을 입력해 주세요. (YYYY-MM)`);
    return;
  }
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(raw)) {
    throw new Error(`${label} '${raw}' 형식이 올바르지 않습니다. YYYY-MM(예: 2026-08) 으로 입력해 주세요.`);
  }
  const year = Number(raw.slice(0, 4));
  if (year < MIN_YEAR || year > MAX_YEAR) {
    throw new Error(`${label}의 연도(${year})가 올바르지 않습니다. ${MIN_YEAR}~${MAX_YEAR} 사이로 입력해 주세요.`);
  }
};
